import { formatDate, formatTime, formatVehicleCategory } from "./format.service";

const CHANGE_CONFIRMATION_FIELDS = [
  "from",
  "to",
  "dateTime",
  "paxCount",
  "hourly",
  "serviceType",
  "vehicle",
  "childSeatsCount",
  "totalCharges"
];

const STATUSES_WITHOUT_CHANGE_CONFIRMATION = ["PNDG", "EXPIRED"];

const PASSENGER_TITLE_BY_GENDER = {
  male: "Mr.",
  female: "Mrs."
};

function getTitleByGender(gender) {
  return PASSENGER_TITLE_BY_GENDER[gender] || "Mx.";
}

/**
 * Decides whether an edited transfer reservation needs to go through the
 * ConfirmChanges modal before saving: only reservations in a status other
 * than PNDG/EXPIRED, and only when one of the fields that actually needs
 * supplier/customer confirmation (as opposed to purely informational
 * fields like notes or contact details) was modified.
 *
 * @param {string} reservationStatus
 * @param {Object} originalDetails - result of toTransferDetails() for the original reservation
 * @param {Object} requestedDetails - result of toTransferDetails() for the edited reservation
 * @returns {boolean}
 */
export function requiresChangeConfirmation(
  reservationStatus,
  originalDetails,
  requestedDetails
) {
  if (STATUSES_WITHOUT_CHANGE_CONFIRMATION.includes(reservationStatus)) {
    return false;
  }
  return CHANGE_CONFIRMATION_FIELDS.some(
    key => originalDetails[key] !== requestedDetails[key]
  );
}

/**
 * Maps an external experience reservation (plus its booking/pricing data)
 * into the flat shape expected by the ConfirmChanges modal, used to diff
 * original vs requested values before saving an edited reservation.
 *
 * @param {Object} reservationData - reservation object (or a snapshot copy)
 * @param {Object} bookingData - booking object (or a snapshot copy)
 * @param {Object} pricingData - pricing object (or a snapshot copy)
 * @returns {Object}
 */
export function mapBookingForComparison(reservationData, bookingData, pricingData) {
  if (!reservationData || !bookingData) return {};

  const contact = bookingData.contact || {};

  return {
    paxCount: (reservationData.passengers || []).length || null,
    paxName: contact.fullName || null,
    email: contact.email || null,
    contactNumber: contact.phone || null,
    specialRequirements: bookingData.pickupNotes || contact.notes || null,
    totalCharges: pricingData ? pricingData.retail : null
  };
}

/**
 * Maps a transfer reservation (plus its populated service) into the flat
 * shape expected by the ConfirmChanges modal, used to diff original vs
 * requested values before saving an edited reservation. Covers every
 * attribute editable from the EditTransfers edit form.
 *
 * @param {Object} reservationData - reservation object (or a snapshot copy)
 * @param {Object} serviceData - populated service object (or a snapshot copy)
 * @returns {Object}
 */
export function toTransferDetails(reservationData, serviceData) {
  if (!reservationData) return {};

  const primaryPassenger =
    (reservationData.passengers || []).find(passenger => passenger.primary) ||
    (reservationData.passengers || [])[0] ||
    {};

  return {
    from: reservationData.from,
    to: reservationData.type !== "hourly" ? reservationData.to : null,
    dateTime: reservationData.pickup_date
      ? `${formatDate(reservationData.pickup_date)} at ${formatTime(
          reservationData.pickup_time
        )}`
      : null,
    paxCount: reservationData.passengersCount,
    flightNumber: ["arrival", "departure"].includes(reservationData.type)
      ? reservationData.flight_number || null
      : null,
    railLine:
      reservationData.type === "station"
        ? reservationData.number_stations || null
        : null,
    cruiseShip:
      reservationData.type === "port"
        ? reservationData.cruise_line || null
        : null,
    hourly:
      reservationData.type === "hourly"
        ? reservationData.number_hourly || null
        : null,
    serviceType:
      serviceData && serviceData.vehicle
        ? `${formatVehicleCategory(serviceData.vehicle.category)} ${
            serviceData.vehicle.seats
          } Seats`
        : null,
    vehicle:
      serviceData && serviceData.vehicle
        ? serviceData.vehicle.make_label
        : null,
    paxName: primaryPassenger.fullName
      ? `${getTitleByGender(primaryPassenger.gender)} ${
          primaryPassenger.fullName
        }`
      : null,
    email: primaryPassenger.email || null,
    contactNumber: primaryPassenger.phone || null,
    pickupSign:
      (reservationData.pickupSign && reservationData.pickupSign.text) || null,
    specialRequirements: reservationData.special_requirements,
    childSeatsCount: reservationData.childSeatsCount || null,
    bookingReference: reservationData.booking_reference || null,
    totalCharges:
      reservationData.pricing && reservationData.pricing.totals
        ? reservationData.pricing.totals.gross_price
        : null
  };
}
